import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Camera, ArrowLeft, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * QR Check-in dialog with two modes:
 *   1. "Show QR" — renders the current branch's QR code for staff to scan.
 *      The QR encodes {origin}/qr/checkin?b=<branch_id>&s=<secret> and
 *      auto-refreshes via server polling every `auto_regen_secs`.
 *   2. "Scan QR" — activates the device camera to scan a QR (staff
 *      scanning on their own device). On success, navigates to the QR
 *      landing page which will handle the actual check-in.
 */
export { ShowQRPanel, ScanQRPanel };

export default function QRCheckinDialog({ open, onOpenChange, branchId, branchName }) {
  const [mode, setMode] = useState(null);   // null | "show" | "scan"

  // Reset mode whenever dialog closes.
  useEffect(() => {
    if (!open) setMode(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="qr-checkin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode && (
              <button
                type="button"
                onClick={() => setMode(null)}
                className="p-1 rounded hover:bg-muted"
                aria-label="Back"
                data-testid="qr-back-btn"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <QrCode size={20} weight="duotone" />
            QR Check-in
            <Badge variant="outline" className="ml-auto text-[10px]">{branchName || "Branch"}</Badge>
          </DialogTitle>
          <DialogDescription>
            {!mode && "Choose how you want to check in."}
            {mode === "show"  && "Have staff scan this code on their phone."}
            {mode === "scan"  && "Point your camera at a branch QR to check in."}
          </DialogDescription>
        </DialogHeader>

        {!mode && (
          <div className="grid gap-3 py-2">
            <Button
              variant="default"
              className="h-24 justify-start gap-4 text-left"
              onClick={() => setMode("show")}
              data-testid="qr-mode-show"
            >
              <QrCode size={28} weight="duotone" />
              <div>
                <div className="text-base font-semibold">Show QR Code</div>
                <div className="text-xs opacity-80">Display the branch code for staff to scan.</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-24 justify-start gap-4 text-left"
              onClick={() => setMode("scan")}
              data-testid="qr-mode-scan"
            >
              <Camera size={28} weight="duotone" />
              <div>
                <div className="text-base font-semibold">Scan QR Code</div>
                <div className="text-xs opacity-70">Use your camera to check yourself in.</div>
              </div>
            </Button>
          </div>
        )}

        {mode === "show" && <ShowQRPanel branchId={branchId} />}
        {mode === "scan" && <ScanQRPanel onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------

function ShowQRPanel({ branchId }) {
  const [tok, setTok] = useState(null);
  const [err, setErr] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const fetchToken = async () => {
    try {
      const { data } = await api.get(`/qr/branch/${branchId}/token`);
      setTok(data);
      setErr(null);
      setCountdown(Number(data.auto_regen_secs) || 0);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    fetchToken();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
     
  }, [branchId]);

  // Auto-refresh countdown — when the branch has qr_auto_regen_secs > 0
  // we display a countdown and refetch (which triggers a server-side
  // rotation only if the admin has already rotated it; the token
  // itself is stable until an admin rotates it via Settings).
  useEffect(() => {
    if (!tok || !tok.auto_regen_secs) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          fetchToken();
          return Number(tok.auto_regen_secs) || 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
     
  }, [tok?.rotated_at]);

  const qrValue = useMemo(() => {
    if (!tok) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/qr/checkin?b=${encodeURIComponent(tok.branch_id)}&s=${encodeURIComponent(tok.secret)}`;
  }, [tok]);

  if (err) {
    return (
      <div className="p-4 rounded border border-destructive/40 bg-destructive/5 text-sm text-destructive" data-testid="qr-show-error">
        {err}
      </div>
    );
  }
  if (!tok) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading QR…</div>;
  }

  return (
    <div className="flex flex-col items-center gap-4 py-2" data-testid="qr-show-panel">
      <div className="p-4 bg-white rounded-lg border" data-testid="qr-code-image">
        <QRCodeSVG value={qrValue} size={240} level="M" includeMargin={false} />
      </div>
      <div className="text-xs text-muted-foreground text-center">
        Branch: <span className="font-medium text-foreground">{tok.branch_name}</span>
        {tok.gps_enabled && (
          <> · GPS geofence <span className="font-medium text-foreground">{tok.gps_radius_m}m</span></>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={fetchToken}
        data-testid="qr-refresh-btn"
        className="gap-1"
      >
        <ArrowsClockwise size={14} />
        {tok.auto_regen_secs > 0
          ? `Refreshes in ${countdown}s`
          : "Refresh"}
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------

function ScanQRPanel({ onClose }) {
  const [status, setStatus] = useState("starting");
  const [err, setErr] = useState(null);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const readerId = "qr-reader-region";

  useEffect(() => {
    let stopped = false;
    let mounted = true;
    const start = async () => {
      // Feature-detect getUserMedia first — headless / desktop /
      // permission-denied all fall in here so we never even try to
      // construct Html5Qrcode (which itself throws in some browsers
      // and unmounts the sibling reader div).
      const hasCam = typeof navigator !== "undefined"
        && navigator.mediaDevices
        && typeof navigator.mediaDevices.getUserMedia === "function";
      if (!hasCam) {
        if (mounted) {
          setErr("Camera API not available on this device or browser.");
          setStatus("error");
        }
        return;
      }
      try {
        const html5 = new Html5Qrcode(readerId, { verbose: false });
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 240 },
          (decodedText) => {
            if (stopped) return;
            stopped = true;
            handleDecoded(decodedText);
          },
          () => { /* per-frame errors — noisy, ignore */ }
        );
        startedRef.current = true;
        if (mounted) setStatus("running");
      } catch (e) {
        if (mounted) {
          setErr(e?.message || String(e));
          setStatus("error");
        }
      }
    };
    // Defer to next tick so the reader div is definitely in the DOM.
    const t = setTimeout(start, 0);
    return () => {
      mounted = false;
      stopped = true;
      clearTimeout(t);
      // Only stop if start() actually succeeded — html5-qrcode throws
      // "not in scanning state" otherwise and floods the console.
      if (scannerRef.current && startedRef.current) {
        try {
          const p = scannerRef.current.stop();
          if (p && typeof p.catch === "function") {
            p.catch(() => {}).finally(() => {
              try { scannerRef.current?.clear?.(); } catch { /* noop */ }
            });
          }
        } catch { /* noop */ }
      }
    };
     
  }, []);

  const handleDecoded = async (text) => {
    // Extract branch_id + secret from the URL. Accept:
    //   {origin}/qr/checkin?b=<id>&s=<secret>
    // If the QR is any other URL, redirect the browser to it — the
    // remote page will handle it.
    try {
      const url = new URL(text);
      if (url.pathname === "/qr/checkin" && url.searchParams.get("b") && url.searchParams.get("s")) {
        // In-app navigation so we keep the authed session.
        window.location.href = `/qr/checkin?b=${encodeURIComponent(url.searchParams.get("b"))}&s=${encodeURIComponent(url.searchParams.get("s"))}`;
        return;
      }
      toast.error("This QR is not a TherapyFlow attendance code.");
    } catch {
      toast.error("Unrecognised QR content.");
    }
    setTimeout(() => onClose?.(), 800);
  };

  return (
    <div className="flex flex-col gap-2 py-2" data-testid="qr-scan-panel">
      <div id={readerId} className="w-full aspect-square bg-black rounded-lg overflow-hidden" data-testid="qr-scan-region" />
      {status === "starting" && (
        <div className="text-xs text-muted-foreground text-center">Requesting camera…</div>
      )}
      {status === "running" && (
        <div className="text-xs text-muted-foreground text-center">Point at the branch QR code.</div>
      )}
      {status === "error" && (
        <div className="p-3 rounded border border-destructive/40 bg-destructive/5 text-xs text-destructive" data-testid="qr-scan-error">
          Camera unavailable — {err}
        </div>
      )}
    </div>
  );
}
